import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FlaskConical, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { api } from '@/api/api';
import { toast } from 'sonner';

const AuthorRegister = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    town: '',
    country: '',
    occupation: '',
    researcher_role: '',
    first_name: '',
    middle_name: '',
    last_name: ''
  });
  const [alternativeNames, setAlternativeNames] = useState([]);
  const [newAltName, setNewAltName] = useState('');
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    special: false
  });

  const validatePassword = (password) => {
    setPasswordStrength({
      length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'password') {
      validatePassword(value);
    }
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.email.includes('@')) {
      newErrors.email = 'Invalid email address';
    }

    if (formData.password.length < 12) {
      newErrors.password = 'Password must be at least 12 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!Object.values(passwordStrength).every(v => v)) {
      newErrors.password = 'Password does not meet all requirements';
    }

    if (!formData.town.trim()) {
      newErrors.town = 'Town is required';
    }

    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    if (!formData.occupation.trim()) {
      newErrors.occupation = 'Occupation is required';
    }

    if (!formData.researcher_role) {
      newErrors.researcher_role = 'Researcher role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error('Please fix the form errors');
      return;
    }

    setLoading(true);
    try {
      const { confirmPassword, ...registerData } = formData;
      // Add alternative names to the registration data
      registerData.alternative_names = alternativeNames;
      const response = await api.registerAuthor(registerData);
      
      toast.success('Registration successful! Please login.');
      navigate('/author/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const PasswordRequirement = ({ met, text }) => (
    <div className="flex items-center gap-2 text-sm">
      {met ? (
        <CheckCircle2 className="h-4 w-4 text-green-600" />
      ) : (
        <X className="h-4 w-4 text-slate-400" />
      )}
      <span className={met ? 'text-green-700' : 'text-slate-500'}>{text}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader className="text-center">
            <Link to="/" className="flex justify-center mb-4">
              <FlaskConical className="h-12 w-12 text-blue-600" />
            </Link>
            <CardTitle className="text-2xl">Author Registration</CardTitle>
            <p className="text-sm text-slate-600 mt-2">
              Create your Evidentica author profile
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Username & Email */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="username">Username *</Label>
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => handleChange('username', e.target.value)}
                    placeholder="johndoe"
                    required
                  />
                  {errors.username && (
                    <p className="text-sm text-red-600 mt-1">{errors.username}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="john@university.edu"
                    required
                  />
                  {errors.email && (
                    <p className="text-sm text-red-600 mt-1">{errors.email}</p>
                  )}
                </div>
              </div>

              {/* Name Fields */}
              <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h3 className="text-sm font-semibold text-slate-700">Full Name Information</h3>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => handleChange('first_name', e.target.value)}
                      placeholder="John"
                    />
                  </div>

                  <div>
                    <Label htmlFor="middle_name">Middle Name</Label>
                    <Input
                      id="middle_name"
                      value={formData.middle_name}
                      onChange={(e) => handleChange('middle_name', e.target.value)}
                      placeholder="Michael"
                    />
                  </div>

                  <div>
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => handleChange('last_name', e.target.value)}
                      placeholder="Smith"
                    />
                  </div>
                </div>

                {/* Alternative Names */}
                <div>
                  <Label htmlFor="alt_name">Alternative Names (optional)</Label>
                  <p className="text-xs text-slate-500 mb-2">Add other name variations you publish under</p>
                  <div className="flex gap-2 mb-2">
                    <Input
                      id="alt_name"
                      value={newAltName}
                      onChange={(e) => setNewAltName(e.target.value)}
                      placeholder="e.g., J.M. Smith"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (newAltName.trim()) {
                            setAlternativeNames([...alternativeNames, newAltName.trim()]);
                            setNewAltName('');
                          }
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (newAltName.trim()) {
                          setAlternativeNames([...alternativeNames, newAltName.trim()]);
                          setNewAltName('');
                        }
                      }}
                    >
                      Add
                    </Button>
                  </div>
                  {alternativeNames.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {alternativeNames.map((name, idx) => (
                        <div key={idx} className="flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm">
                          {name}
                          <button
                            type="button"
                            onClick={() => setAlternativeNames(alternativeNames.filter((_, i) => i !== idx))}
                            className="text-blue-900 hover:text-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Password */}
              <div>
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="••••••••••••"
                  required
                />
                {errors.password && (
                  <p className="text-sm text-red-600 mt-1">{errors.password}</p>
                )}
                
                {/* Password Requirements */}
                <div className="mt-3 p-3 bg-slate-50 rounded space-y-1">
                  <p className="text-sm font-semibold text-slate-700 mb-2">Password Requirements:</p>
                  <PasswordRequirement met={passwordStrength.length} text="At least 12 characters" />
                  <PasswordRequirement met={passwordStrength.uppercase} text="One uppercase letter" />
                  <PasswordRequirement met={passwordStrength.lowercase} text="One lowercase letter" />
                  <PasswordRequirement met={passwordStrength.digit} text="One digit" />
                  <PasswordRequirement met={passwordStrength.special} text="One special character (!@#$%...)" />
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  placeholder="••••••••••••"
                  required
                />
                {errors.confirmPassword && (
                  <p className="text-sm text-red-600 mt-1">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Town & Country */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="town">Town/City *</Label>
                  <Input
                    id="town"
                    value={formData.town}
                    onChange={(e) => handleChange('town', e.target.value)}
                    placeholder="Boston"
                    required
                  />
                  {errors.town && (
                    <p className="text-sm text-red-600 mt-1">{errors.town}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="country">Country *</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                    placeholder="USA"
                    required
                  />
                  {errors.country && (
                    <p className="text-sm text-red-600 mt-1">{errors.country}</p>
                  )}
                </div>
              </div>

              {/* Occupation */}
              <div>
                <Label htmlFor="occupation">Occupation *</Label>
                <Input
                  id="occupation"
                  value={formData.occupation}
                  onChange={(e) => handleChange('occupation', e.target.value)}
                  placeholder="Medical Researcher"
                  required
                />
                {errors.occupation && (
                  <p className="text-sm text-red-600 mt-1">{errors.occupation}</p>
                )}
              </div>

              {/* Researcher Role */}
              <div>
                <Label htmlFor="researcher_role">Researcher Role *</Label>
                <Select
                  value={formData.researcher_role}
                  onValueChange={(value) => handleChange('researcher_role', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Principal Investigator">Principal Investigator</SelectItem>
                    <SelectItem value="Co-Investigator">Co-Investigator</SelectItem>
                    <SelectItem value="Research Fellow">Research Fellow</SelectItem>
                    <SelectItem value="Postdoctoral Researcher">Postdoctoral Researcher</SelectItem>
                    <SelectItem value="PhD Student">PhD Student</SelectItem>
                    <SelectItem value="Research Assistant">Research Assistant</SelectItem>
                    <SelectItem value="Clinician Researcher">Clinician Researcher</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {errors.researcher_role && (
                  <p className="text-sm text-red-600 mt-1">{errors.researcher_role}</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Creating Account...' : 'Create Author Account'}
              </Button>

              <p className="text-center text-sm text-slate-600">
                Already have an account?{' '}
                <Link to="/author/login" className="text-blue-600 hover:underline">
                  Login here
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthorRegister;
